import { useLocation } from "react-router";

import {
  mapAnalysisProvidersTableRowData,
  mapCompaniesTableRowData,
  mapDriversFilesTableRowData,
  mapDriversTableRowData,
  mapIntegrationsApiTableRowData,
  mapIntegrationsTableRowData,
  mapUsersInvitationsTableRowData,
  mapUserTableRowData,
  mapVehicleFilesTableRowData,
  mapVehiclesTableRowData
} from "@/Pages/utils";
import {
  getTableStoreByRoute,
  useTableStoreByRoute
} from "@/stores/useTableStore";

import useAxiosPrivate from "../../api/hooks/useAxiosPrivate";
import { MIN_CHAR_LENGTH_IN_SEARCH } from "../../components/Search/constants";
import useTableDataFunctions from "../../components/Table/hooks/useTableDataFunctions";
import { ALERT_STATUS } from "../../context/alert/types";
import { ROUTES } from "..";
import useResponse from "./useResponse";

const useRefreshTableData = () => {
  const { getData } = useAxiosPrivate();
  const location = useLocation();
  const {
    routeWithoutSearch,
    setPageDataState,
    getFiltersAndFormFilterQueryParams
  } = useTableDataFunctions();
  const { handleResponse } = useResponse();

  const { setDataAndPagination } = useTableStoreByRoute(
    location.pathname as ROUTES
  );

  const tableStore = getTableStoreByRoute(location.pathname as ROUTES);

  const companiesRoute = location.pathname === ROUTES.Companies;

  const setTableDataState = (pathname: string, data: any) => {
    const dataItems = data?.items;

    const companiesPagination = {
      page: 1,
      totalPages: 1,
      pageSize: 25,
      totalCount: mapCompaniesTableRowData(data).length || 0,
      hasPrevious: false,
      hasNext: false
    };

    switch (pathname) {
      case ROUTES.Users:
        return setDataAndPagination(mapUserTableRowData(dataItems), data);

      case ROUTES.UsersInvitations:
        return setDataAndPagination(
          mapUsersInvitationsTableRowData(dataItems),
          data
        );
      case ROUTES.VehicleFiles:
        return setDataAndPagination(
          mapVehicleFilesTableRowData(dataItems),
          data
        );
      case ROUTES.DriverFiles:
        return setDataAndPagination(
          mapDriversFilesTableRowData(dataItems),
          data
        );
      case ROUTES.Companies:
        return setDataAndPagination(
          mapCompaniesTableRowData(data),
          companiesPagination
        );
      case ROUTES.Vehicles:
        return setDataAndPagination(mapVehiclesTableRowData(dataItems), data);
      case ROUTES.Drivers:
        return setDataAndPagination(mapDriversTableRowData(dataItems), data);
      case ROUTES.Integrations:
        return setDataAndPagination(
          mapIntegrationsTableRowData(dataItems),
          data
        );
      case ROUTES.IntegrationsApi:
        return setDataAndPagination(
          mapIntegrationsApiTableRowData(dataItems),
          data
        );
      case ROUTES.AnalysisProviders:
        return setDataAndPagination(
          mapAnalysisProvidersTableRowData(dataItems),
          data
        );

      default:
        return setDataAndPagination(dataItems, data);
    }
  };

  const refreshTableData = async (
    setNewDetails?: any,
    searchParams?: string,
    fromChangeSelectedCompany?: boolean
  ) => {
    const currentState = tableStore.getState();

    const { params, loading, setLoading, setParams } = currentState;

    const filterParams = params.filters;
    const sortedColumn = params.sortedColumn;

    const paginationState = currentState.pagination;

    const pageToShow = paginationState.page.toString();
    const rowsCount = paginationState.pageSize;

    const pageNumberInQueryParams = fromChangeSelectedCompany
      ? "1"
      : pageToShow;

    const searchQueryString =
      searchParams && searchParams.length >= MIN_CHAR_LENGTH_IN_SEARCH
        ? `&Contains=${encodeURIComponent(searchParams)}`
        : "";

    const sortingString = sortedColumn
      ? `&OrderBy=${sortedColumn.orderBy}&Descending=${sortedColumn.descending}`
      : "";

    const baseQuery = `${location.pathname}?PageNumber=${pageNumberInQueryParams}&PageSize=${rowsCount}${searchQueryString}${sortingString}`;

    const apiQueryParams =
      baseQuery + getFiltersAndFormFilterQueryParams(filterParams);

    if (searchParams && paginationState.page !== 1) {
      return;
    }

    !loading && setLoading?.(true);

    try {
      const response = await getData(
        routeWithoutSearch(companiesRoute, location) || apiQueryParams
      );

      setPageDataState(
        response,
        companiesRoute,
        () => {},
        mapCompaniesTableRowData
      );

      const apiResponse = companiesRoute
        ? {
            ...response?.data
          }
        : response?.data;

      setTableDataState(location.pathname, apiResponse);

      if (setNewDetails && response.data.items?.[0]) {
        setParams?.({
          selectedTableItem: response.data.items[0]
        });
      }
    } catch (error) {
      const err = error as any;
      if (err) {
        const responseMessage = err.response?.data.message;
        const responseEntriesMessage =
          err.response?.data !== "" &&
          err.response?.data?.entries?.[0]?.message;
        const errorMessage =
          responseEntriesMessage || responseMessage || err.message;
        handleResponse(ALERT_STATUS.Critical, errorMessage);
      }
    } finally {
      setLoading?.(false);
    }
  };

  return { refreshTableData };
};

export default useRefreshTableData;
